import { getCurrentUser, setUpTOTP } from '@aws-amplify/auth';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import QRCode from 'v2/components/QRCode/QRCode';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useWizardStepContext from 'v2/components/WizardModal/useWizardStepContext';

const STEP_INDEX = 0;

const SetupStep: React.FC = () => {
  const { translation } = useTranslation();
  const { goToNext, setFooterContent, steps, activeStep } = useWizardStepContext();
  const { showSnackbar } = useSnackbarContext();
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [setupError, setSetupError] = useState(false);

  const setup = useCallback(async () => {
    setSetupError(false);
    setQrCodeUrl('');
    try {
      const currentUser = await getCurrentUser();
      const secretCode = await setUpTOTP();
      const url = secretCode.getSetupUri('ATLAS', currentUser.signInDetails?.loginId);
      setQrCodeUrl('' + url);
    } catch (err) {
      console.error(err);
      setSetupError(true);
      showSnackbar(translation.error_generating_qr_code, { severity: 'error' });
    }
  }, [showSnackbar, translation.error_generating_qr_code]);

  useEffect(() => {
    if (activeStep !== STEP_INDEX) return;
    setup();
  }, [activeStep, setup]);

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button id="setup-next-button" variant="contained" onClick={goToNext} endIcon={<ChevronRightIcon />}>
          {translation.next}
        </Button>
      </Box>
    );
  }, [setFooterContent, steps, translation, goToNext]);

  return (
    <Stack gap={3} sx={{ p: 2 }}>
      <Typography variant="body2">
        {translation.open_or_install_an_authenticator_app_to_your_liking_and_use_it_to_scan_the_qr_code_shown_below}
      </Typography>
      {qrCodeUrl ? (
        <QRCode value={qrCodeUrl} sx={{ alignSelf: 'center' }} />
      ) : setupError ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <Button id="setup-retry-button" variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={setup}>
            {translation.retry}
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={20} />
          <Typography variant="body2">{translation.generating_qr}</Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default SetupStep;
